import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { TableHeader } from "../../../common/components/functional/GroupedTable/styled";
import TableLinkRow from "../../../common/components/functional/tableLinkRow/TableLinkRow";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import { getCoreUserFullName } from "../../../common/utils/users";
import i18n from "../../../i18n";
import {
  fetchCandidateIntroductions,
  fetchCoreUsers,
} from "../../talents/talentsSlice";
import {
  ALL_WITHOUT_ORDER_INTRODUCTION_STATUSES,
  TalentIntroduction,
} from "../../talents/types/queries";

import { IntroductionStatus } from "./IntroductionStatus";

type Props = {
  talentId: string;
  appliedBundleIds: ReadonlySet<string>;
  withOrderIntroductions: TalentIntroduction[];
};

type IntroductionRow = {
  id: string;
  url?: string;
  title: ReactNode;
  workLocation: string;
  workStarts?: string;
  workEnds?: string;
  customer: string;
  handler: string;
  lastStatusUpdatedAt: string;
  status: ReactNode;
};

const formatDate = (value?: number | null) =>
  value ? format(new Date(value), leadingZerosDateFormat) : "";

const IntroductionTable = ({
  rows,
  titleColumn,
  showDateHeaders = true,
}: {
  rows: IntroductionRow[];
  titleColumn: string;
  showDateHeaders?: boolean;
}) => {
  const { t } = useTranslation("talentProfile");

  const columns: {
    key: keyof IntroductionRow;
    title: string;
    width: string;
  }[] = [
    { key: "title", title: titleColumn, width: "260px" },
    {
      key: "workLocation",
      title: t("introductions.columns.workLocation"),
      width: "255px",
    },
    {
      key: "workStarts",
      title: showDateHeaders ? t("introductions.columns.workStarts") : "",
      width: "90px",
    },
    {
      key: "workEnds",
      title: showDateHeaders ? t("introductions.columns.workEnds") : "",
      width: "90px",
    },
    {
      key: "customer",
      title: t("introductions.columns.customer"),
      width: "245px",
    },
    {
      key: "handler",
      title: t("introductions.columns.handler"),
      width: "200px",
    },
    {
      key: "lastStatusUpdatedAt",
      title: t("introductions.columns.lastStatusUpdatedAt"),
      width: "100px",
    },
    { key: "status", title: t("introductions.columns.status"), width: "150px" },
  ];

  return (
    <Box sx={{ background: "#FFFFFF", borderRadius: "8px", overflowX: "auto" }}>
      <Table>
        <TableHeader allowBorders={false}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.key}
                lang={i18n.language || "fi"}
                sx={{
                  width: column.width,
                  paddingLeft: index === 0 ? "26px" : "5px",
                  paddingRight: "5px",
                }}
              >
                {column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((row, index) => {
              const cells = columns.map((column, columnIndex) => (
                <TableCell
                  key={column.key}
                  align="left"
                  valign="middle"
                  sx={{
                    paddingLeft: columnIndex === 0 ? "26px" : "5px",
                    paddingRight: "5px",
                  }}
                >
                  {row[column.key]}
                </TableCell>
              ));
              const sx = {
                backgroundColor: index % 2 === 0 ? "#FAFAFB" : "#FFFFFF",
              };

              return row.url ? (
                <TableLinkRow key={row.id} to={row.url} TableRowProps={{ sx }}>
                  {cells}
                </TableLinkRow>
              ) : (
                <TableRow key={row.id} sx={sx}>
                  {cells}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ paddingLeft: "26px" }}>
                <Typography>{t("common:noItems")}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export const TalentIntroductions = ({
  talentId,
  appliedBundleIds,
  withOrderIntroductions,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");
  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const [withoutOrder, setWithoutOrder] = useState<TalentIntroduction[]>([]);
  const [lastStatusUpdatedByNames, setLastStatusUpdatedByNames] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!talentId) return;

    dispatch(
      fetchCandidateIntroductions({
        talentId,
        pageSize: "500",
        types: "WithoutOrder",
        statuses: ALL_WITHOUT_ORDER_INTRODUCTION_STATUSES,
      }),
    ).then((action) => {
      if (!isFulfilled(action)) return;

      const introductions = action.payload.data;
      setWithoutOrder(introductions);
      const userIds = Array.from(
        new Set(
          introductions
            .map(({ lastStatusUpdatedBy }) => lastStatusUpdatedBy)
            .filter((id): id is string => Boolean(id)),
        ),
      );
      setLastStatusUpdatedByNames({});

      if (userIds.length) {
        dispatch(fetchCoreUsers({ userIds })).then((usersAction) => {
          if (isFulfilled(usersAction)) {
            setLastStatusUpdatedByNames(
              Object.fromEntries(
                usersAction.payload.data.map((user) => [
                  user.id,
                  getCoreUserFullName(user),
                ]),
              ),
            );
          }
        });
      }
    });
  }, [dispatch, talentId]);

  const withOrderRows = useMemo<IntroductionRow[]>(
    () =>
      withOrderIntroductions
        .filter(({ bundleId }) => !bundleId || !appliedBundleIds.has(bundleId))
        .map((introduction) => ({
          id:
            introduction.bundleId ??
            `${introduction.workLocationId}_${introduction.talentId}`,
          url: introduction.bundleId
            ? `/home/orders/${introduction.bundleId}`
            : undefined,
          title: introduction.orderTitle ?? introduction.bundleId ?? "",
          workLocation: introduction.workLocationName,
          workStarts: formatDate(introduction.workStarts),
          workEnds: formatDate(introduction.workEnds),
          customer: introduction.customerOrgName,
          handler:
            introduction.orderHandlers?.map(({ name }) => name).join(", ") ??
            "",
          lastStatusUpdatedAt: formatDate(
            introduction.lastStatusUpdatedAt ?? introduction.introducedAt,
          ),
          status: <IntroductionStatus {...introduction} />,
        })),
    [appliedBundleIds, withOrderIntroductions],
  );

  const withoutOrderRows = useMemo<IntroductionRow[]>(
    () =>
      withoutOrder.map((introduction) => ({
        id: `${introduction.workLocationId}_${introduction.talentId}`,
        title: introduction.workTitleIds
          .map((id) => translatedWorkTitles[id] ?? id)
          .join(", "),
        workLocation: introduction.workLocationName,
        customer: introduction.customerOrgName,
        handler: introduction.lastStatusUpdatedBy
          ? lastStatusUpdatedByNames[introduction.lastStatusUpdatedBy] ?? ""
          : "",
        lastStatusUpdatedAt: formatDate(
          introduction.lastStatusUpdatedAt ?? introduction.introducedAt,
        ),
        status: <IntroductionStatus {...introduction} />,
      })),
    [lastStatusUpdatedByNames, translatedWorkTitles, withoutOrder],
  );

  return (
    <>
      <Box sx={{ marginTop: "32px" }}>
        <Typography
          variant="header"
          sx={{ fontWeight: 600, marginBottom: "16px" }}
        >
          {t("introductions.withOrder").toUpperCase()}
        </Typography>
      </Box>
      <IntroductionTable
        rows={withOrderRows}
        titleColumn={t("introductions.columns.order")}
      />

      <Box sx={{ marginTop: "32px" }}>
        <Typography
          variant="header"
          sx={{ fontWeight: 600, marginBottom: "16px" }}
        >
          {t("introductions.withoutOrder").toUpperCase()}
        </Typography>
      </Box>
      <IntroductionTable
        rows={withoutOrderRows}
        titleColumn={t("introductions.columns.workTitles")}
        showDateHeaders={false}
      />
    </>
  );
};
